import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  fetchStateImpacts,
  fetchBAImpacts,
  fetchGeneratorImpacts,
  type StateImpact,
  type BAImpact,
  type GeneratorImpact,
} from "../../api/pipeline";

type Tab = "states" | "bas" | "generators";

function formatDALYs(val: number): string {
  return Math.abs(val).toFixed(2);
}

function formatUSD(val: number): string {
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(0)}k`;
  return `$${abs.toFixed(0)}`;
}

function formatMW(val: number): string {
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)} GW`;
  return `${val.toFixed(0)} MW`;
}

const PipelineImpactPage: React.FC = () => {
  const [tab, setTab] = useState<Tab>("states");
  const [generatorState, setGeneratorState] = useState<string>("");
  const [generatorPage, setGeneratorPage] = useState(1);

  const { data: stateData, isLoading: statesLoading } = useQuery({
    queryKey: ["pipelineStateImpacts"],
    queryFn: fetchStateImpacts,
    staleTime: 5 * 60 * 1000,
  });

  const { data: baData, isLoading: basLoading } = useQuery({
    queryKey: ["pipelineBAImpacts"],
    queryFn: fetchBAImpacts,
    staleTime: 5 * 60 * 1000,
    enabled: tab === "bas",
  });

  const { data: generatorData, isLoading: generatorsLoading } = useQuery({
    queryKey: ["pipelineGeneratorImpacts", generatorState, generatorPage],
    queryFn: () =>
      fetchGeneratorImpacts({
        state: generatorState || undefined,
        page: generatorPage,
        page_size: 25,
      }),
    staleTime: 5 * 60 * 1000,
    enabled: tab === "generators",
  });

  const totals = stateData?.totals;

  const stateOptions = useMemo(() => {
    if (!stateData?.states) return [];
    return stateData.states
      .filter((s) => s.generator_count > 0)
      .map((s) => ({ id: s.state_id, name: s.state_name }));
  }, [stateData]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/grid/states"
          className="text-caption text-text-muted hover:text-text-primary transition-colors mb-2 inline-block"
        >
          &larr; Back to pipeline dashboard
        </Link>
        <h1 className="text-h1 font-bold text-text-primary">
          Pipeline Impact Analysis
        </h1>
        <p className="text-body text-text-muted mt-2 max-w-2xl">
          Full lifecycle impact assessments for planned solar and wind generators
          across the US. Every number represents a complete LCA model run — not a screening estimate.
        </p>
      </div>

      {/* Hero metrics */}
      {totals && totals.generator_count > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-surface-800 rounded-xl p-5 border border-border">
            <p className="text-caption text-text-muted">Generators Analyzed</p>
            <p className="text-h2 font-bold text-text-primary">
              {totals.generator_count.toLocaleString()}
            </p>
          </div>
          <div className="bg-surface-800 rounded-xl p-5 border border-border">
            <p className="text-caption text-text-muted">Total Capacity</p>
            <p className="text-h2 font-bold text-quantum-400">
              {formatMW(totals.total_capacity_mw)}
            </p>
          </div>
          <div className="bg-surface-800 rounded-xl p-5 border border-health-500/20">
            <p className="text-caption text-text-muted">DALYs Avoided</p>
            <p className="text-h2 font-bold text-health-400">
              {formatDALYs(totals.dalys_avoided)}
            </p>
            <p className="text-[10px] text-text-muted mt-1">
              Disability-adjusted life years
            </p>
          </div>
          <div className="bg-surface-800 rounded-xl p-5 border border-ecosystem-500/20">
            <p className="text-caption text-text-muted">Ecosystem Impact</p>
            <p className="text-h2 font-bold text-ecosystem-400">
              {Math.abs(totals.pdfs_avoided).toFixed(6)}
            </p>
            <p className="text-[10px] text-text-muted mt-1">
              species.yr avoided
            </p>
          </div>
          <div className="bg-surface-800 rounded-xl p-5 border border-green-500/20">
            <p className="text-caption text-text-muted">Economic Value</p>
            <p className="text-h2 font-bold text-green-400">
              {formatUSD(totals.usd_avoided)}
            </p>
          </div>
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex items-center gap-1 mb-6 border-b border-border">
        {(["states", "bas", "generators"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-body-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === t
                ? "border-quantum-500 text-quantum-400"
                : "border-transparent text-text-muted hover:text-text-primary"
            }`}
          >
            {t === "states" ? "By State" : t === "bas" ? "By BA" : "Generators"}
          </button>
        ))}
      </div>

      {/* States tab */}
      {tab === "states" && (
        <div>
          {statesLoading && <Spinner />}
          {stateData && (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="pb-3 pr-4 text-text-muted font-medium">State</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">Generators</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">Capacity</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">DALYs Avoided</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">Ecosystem</th>
                    <th className="pb-3 text-text-muted font-medium text-right">Economic Value</th>
                  </tr>
                </thead>
                <tbody>
                  {stateData.states.map((s) => (
                    <tr
                      key={s.state_id}
                      className="border-b border-border/50 hover:bg-surface-800/50 transition-colors"
                    >
                      <td className="py-3 pr-4">
                        <Link
                          to={`/generators?state=${s.state_id}&status=P,L,T,U,V,TS`}
                          className="text-quantum-400 hover:text-quantum-300"
                        >
                          {s.state_name}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-right text-text-primary">
                        {s.generator_count}
                      </td>
                      <td className="py-3 pr-4 text-right text-text-primary">
                        {formatMW(s.total_capacity_mw)}
                      </td>
                      <td className="py-3 pr-4 text-right text-health-400 font-medium">
                        {formatDALYs(s.dalys_avoided)}
                      </td>
                      <td className="py-3 pr-4 text-right text-ecosystem-400">
                        {Math.abs(s.pdfs_avoided).toFixed(6)}
                      </td>
                      <td className="py-3 text-right text-green-400 font-medium">
                        {formatUSD(s.usd_avoided)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* BAs tab */}
      {tab === "bas" && (
        <div>
          {basLoading && <Spinner />}
          {baData && (
            <div className="overflow-x-auto">
              <table className="w-full text-body-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="pb-3 pr-4 text-text-muted font-medium">BA Code</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">Generators</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">Capacity</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">DALYs Avoided</th>
                    <th className="pb-3 pr-4 text-text-muted font-medium text-right">Ecosystem</th>
                    <th className="pb-3 text-text-muted font-medium text-right">Economic Value</th>
                  </tr>
                </thead>
                <tbody>
                  {baData.bas.map((b) => (
                    <tr
                      key={b.ba_code}
                      className="border-b border-border/50 hover:bg-surface-800/50 transition-colors"
                    >
                      <td className="py-3 pr-4">
                        <Link
                          to={`/grid/${b.ba_code}`}
                          className="text-quantum-400 hover:text-quantum-300"
                        >
                          {b.ba_code}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 text-right text-text-primary">
                        {b.generator_count}
                      </td>
                      <td className="py-3 pr-4 text-right text-text-primary">
                        {formatMW(b.total_capacity_mw)}
                      </td>
                      <td className="py-3 pr-4 text-right text-health-400 font-medium">
                        {formatDALYs(b.dalys_avoided)}
                      </td>
                      <td className="py-3 pr-4 text-right text-ecosystem-400">
                        {Math.abs(b.pdfs_avoided).toFixed(6)}
                      </td>
                      <td className="py-3 text-right text-green-400 font-medium">
                        {formatUSD(b.usd_avoided)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Generators tab */}
      {tab === "generators" && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <select
              value={generatorState}
              onChange={(e) => {
                setGeneratorState(e.target.value);
                setGeneratorPage(1);
              }}
              className="input w-48 text-body-sm"
            >
              <option value="">All States</option>
              {stateOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {generatorData && (
              <span className="text-caption text-text-muted">
                {generatorData.total.toLocaleString()} generators
              </span>
            )}
          </div>

          {generatorsLoading && <Spinner />}
          {generatorData && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-3 pr-4 text-text-muted font-medium">Generator</th>
                      <th className="pb-3 pr-4 text-text-muted font-medium">State</th>
                      <th className="pb-3 pr-4 text-text-muted font-medium text-right">MW</th>
                      <th className="pb-3 pr-4 text-text-muted font-medium text-right">DALYs</th>
                      <th className="pb-3 text-text-muted font-medium text-right">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatorData.generators.map((g) => (
                      <tr
                        key={`${g.plant_id}-${g.generator_id}`}
                        className="border-b border-border/50 hover:bg-surface-800/50 transition-colors"
                      >
                        <td className="py-3 pr-4">
                          <Link
                            to={`/generators/${encodeURIComponent(g.plant_id)}/${encodeURIComponent(g.generator_id)}`}
                            className="text-quantum-400 hover:text-quantum-300"
                          >
                            {g.plant_name || `${g.plant_id}/${g.generator_id}`}
                          </Link>
                          <p className="text-[10px] text-text-muted">
                            {g.tech_type_id === "solar_ground" ? "Solar" : "Wind"}
                            {g.ba_code ? ` / ${g.ba_code}` : ""}
                          </p>
                        </td>
                        <td className="py-3 pr-4 text-text-muted">{g.state_id}</td>
                        <td className="py-3 pr-4 text-right text-text-primary">
                          {g.nameplate_capacity_mw?.toLocaleString() ?? "—"}
                        </td>
                        <td className="py-3 pr-4 text-right text-health-400 font-medium">
                          {g.dalys_avoided != null ? formatDALYs(g.dalys_avoided) : "—"}
                        </td>
                        <td className="py-3 text-right text-green-400 font-medium">
                          {g.usd_avoided != null ? formatUSD(g.usd_avoided) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {generatorData.total > 25 && (
                <div className="flex items-center justify-center gap-3 mt-4">
                  <button
                    onClick={() => setGeneratorPage((p) => Math.max(1, p - 1))}
                    disabled={generatorPage <= 1}
                    className="px-3 py-1.5 text-caption rounded border border-border disabled:opacity-40 hover:bg-surface-800 transition-colors"
                  >
                    Previous
                  </button>
                  <span className="text-caption text-text-muted">
                    Page {generatorPage} of {Math.ceil(generatorData.total / 25)}
                  </span>
                  <button
                    onClick={() => setGeneratorPage((p) => p + 1)}
                    disabled={generatorPage * 25 >= generatorData.total}
                    className="px-3 py-1.5 text-caption rounded border border-border disabled:opacity-40 hover:bg-surface-800 transition-colors"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* CTA */}
      <div className="mt-12 bg-gradient-to-r from-quantum-500/10 to-quantum-500/5 rounded-xl p-8 border border-quantum-500/20 text-center">
        <h2 className="text-h3 font-bold text-text-primary mb-2">
          What's YOUR generator's impact?
        </h2>
        <p className="text-body text-text-muted mb-5 max-w-lg mx-auto">
          Claim your planned generator to get a detailed lifecycle impact report —
          human health, ecosystem quality, and economic value.
        </p>
        <Link
          to="/generators?status=P,L,T,U,V,TS"
          className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-quantum-500 text-white font-medium hover:bg-quantum-400 transition-colors"
        >
          Find Your Generator
        </Link>
      </div>

      {/* Disclaimer */}
      <p className="text-[10px] text-text-muted mt-6">
        Impact data from full lifecycle assessments (LCA) using the TotalView model and ecoinvent database.
        Generators sourced from EIA-860M monthly reports.
        {stateData?.period && ` Data period: ${stateData.period}.`}
      </p>
    </div>
  );
};

const Spinner: React.FC = () => (
  <div className="flex items-center justify-center py-20">
    <div className="w-5 h-5 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default PipelineImpactPage;
